import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiPlay, FiPause, FiRotateCcw, FiArrowLeft } from 'react-icons/fi';

const FirstAidAnimation = ({ scenarioId, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  
  // Animation data for different scenarios
  const animationData = {
    "1": {
      title: "CPR Emergency",
      steps: [
        { title: "Check Responsiveness", duration: 5 },
        { title: "Call for Help", duration: 3 },
        { title: "Open Airway", duration: 4 },
        { title: "Check Breathing", duration: 3 },
        { title: "Chest Compressions", duration: 8 },
        { title: "Rescue Breaths", duration: 7 }
      ],
      totalDuration: 30
    },
    "2": {
      title: "Choking Emergency",
      steps: [
        { title: "Recognize Choking", duration: 4 },
        { title: "Ask to Cough", duration: 3 },
        { title: "Back Blows", duration: 6 },
        { title: "Abdominal Thrusts", duration: 8 },
        { title: "Check Mouth", duration: 3 },
        { title: "Repeat if Necessary", duration: 6 }
      ],
      totalDuration: 30
    },
    "3": {
      title: "Severe Burn Treatment",
      steps: [
        { title: "Stop Burning Process", duration: 4 },
        { title: "Cool Burn with Water", duration: 7 },
        { title: "Remove Constrictive Items", duration: 5 },
        { title: "Cover Burn", duration: 4 },
        { title: "Treat for Shock", duration: 5 },
        { title: "Seek Medical Help", duration: 5 }
      ],
      totalDuration: 30
    },
    "4": {
      title: "Sudden Cardiac Arrest",
      steps: [
        { title: "Check Response", duration: 3 },
        { title: "Call Emergency", duration: 3 },
        { title: "Get AED", duration: 4 },
        { title: "Start CPR", duration: 8 },
        { title: "Use AED", duration: 7 },
        { title: "Continue Until Help Arrives", duration: 5 }
      ],
      totalDuration: 30
    },
    "5": {
      title: "Stroke Emergency",
      steps: [
        { title: "Recognize Stroke Symptoms", duration: 5 },
        { title: "Call Emergency", duration: 3 },
        { title: "Keep Person Comfortable", duration: 5 },
        { title: "Note Time of Symptoms", duration: 4 },
        { title: "Monitor Breathing", duration: 5 },
        { title: "Do Not Give Food/Drink", duration: 8 }
      ],
      totalDuration: 30
    },
    "6": {
      title: "Pediatric Choking for Infants",
      steps: [
        { title: "Recognize Distress", duration: 4 },
        { title: "Support Head & Neck", duration: 5 },
        { title: "Back Blows", duration: 6 },
        { title: "Chest Thrusts", duration: 7 },
        { title: "Check Mouth", duration: 3 },
        { title: "Repeat if Necessary", duration: 5 }
      ],
      totalDuration: 30
    }
  };

  const scenario = animationData[scenarioId] || animationData["2"]; // Default to choking

  useEffect(() => {
    let interval;
    if (isPlaying && progress < scenario.totalDuration) {
      interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 0.1;
          
          // Update current step based on progress
          let elapsed = 0;
          for (let i = 0; i < scenario.steps.length; i++) {
            elapsed += scenario.steps[i].duration;
            if (newProgress <= elapsed) {
              if (currentStep !== i) setCurrentStep(i);
              break;
            }
          }
          
          return newProgress > scenario.totalDuration ? scenario.totalDuration : newProgress;
        });
      }, 100);
    }
    
    return () => clearInterval(interval);
  }, [isPlaying, progress, scenario, currentStep]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleRestart = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentStep(0);
    setTimeout(() => setIsPlaying(true), 100);
  };

  const progressPercentage = (progress / scenario.totalDuration) * 100;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-bold text-purple-800">{scenario.title} Animation</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX size={24} />
          </button>
        </div>
        
        <div className="p-6">
          {/* Animation Container */}
          <div className="bg-gray-100 rounded-lg p-4 mb-6 h-80 flex items-center justify-center relative">
            <AnimationVisualization 
              scenarioId={scenarioId} 
              currentStep={currentStep} 
              progress={progress} 
            />
            
            {/* Progress indicator */}
            <div className="absolute bottom-4 left-4 right-4 bg-white bg-opacity-90 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-100"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            
            {/* Current step label */}
            <div className="absolute top-4 left-4 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              Step {currentStep + 1}: {scenario.steps[currentStep].title}
            </div>
            
            {/* Timer */}
            <div className="absolute top-4 right-4 bg-white bg-opacity-90 px-3 py-1 rounded-full text-sm font-medium">
              {Math.floor(progress)}s / {scenario.totalDuration}s
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex justify-center items-center space-x-4">
            <button 
              onClick={handleRestart}
              className="bg-gray-200 hover:bg-gray-300 rounded-full p-3 transition-colors"
              aria-label="Restart animation"
            >
              <FiRotateCcw size={20} />
            </button>
            
            <button 
              onClick={handlePlayPause}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-full p-4 transition-colors"
              aria-label={isPlaying ? "Pause animation" : "Play animation"}
            >
              {isPlaying ? <FiPause size={24} /> : <FiPlay size={24} />}
            </button>
            
            <button 
              onClick={onClose}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium py-2 px-4 rounded-lg border border-purple-200 hover:border-purple-300"
            >
              <FiArrowLeft /> Back to Results
            </button>
          </div>
          
          {/* Step indicators */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
            {scenario.steps.map((step, index) => (
              <div 
                key={index}
                className={`p-3 rounded-lg border text-sm ${
                  index === currentStep 
                    ? 'bg-purple-100 border-purple-400 text-purple-800 font-medium' 
                    : index < currentStep
                    ? 'bg-gray-100 border-gray-300 text-gray-600'
                    : 'bg-white border-gray-200 text-gray-500'
                }`}
              >
                <div className="flex justify-between items-start">
                  <span className="font-semibold">Step {index + 1}:</span>
                  <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                    {step.duration}s
                  </span>
                </div>
                <div className="mt-1">{step.title}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Component that renders the actual animation based on scenario
const AnimationVisualization = ({ scenarioId, currentStep, progress }) => {
  switch(scenarioId) {
    case "1": // CPR
      return <CPRAnimation currentStep={currentStep} progress={progress} />;
    case "2": // Choking
      return <ChokingAnimation currentStep={currentStep} progress={progress} />;
    case "3": // Burns
      return <BurnsAnimation currentStep={currentStep} progress={progress} />;
    case "4": // Cardiac Arrest
      return <CardiacAnimation currentStep={currentStep} progress={progress} />;
    case "5": // Stroke
      return <StrokeAnimation currentStep={currentStep} progress={progress} />;
    case "6": // Infant Choking
      return <InfantChokingAnimation currentStep={currentStep} progress={progress} />;
    default:
      return <ChokingAnimation currentStep={currentStep} progress={progress} />;
  }
};

// Individual animation components for each scenario
const CPRAnimation = ({ currentStep, progress }) => {
  const compressions = currentStep === 4;
  const breaths = currentStep === 5;
  
  return (
    <div className="relative w-64 h-64">
      {/* Person outline */}
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* Head */}
        <circle cx="100" cy="60" r="25" fill="none" stroke="#4B5563" strokeWidth="2" />
        
        {/* Body */}
        <line x1="100" y1="85" x2="100" y2="140" stroke="#4B5563" strokeWidth="2" />
        
        {/* Arms */}
        <line x1="100" y1="100" x2="70" y2="120" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="100" x2="130" y2="120" stroke="#4B5563" strokeWidth="2" />
        
        {/* Legs */}
        <line x1="100" y1="140" x2="80" y2="170" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="140" x2="120" y2="170" stroke="#4B5563" strokeWidth="2" />
        
        {/* Compression animation */}
        {compressions && (
          <rect 
            x="85" y="95" width="30" height="15" rx="2"
            fill="#EF4444" 
            opacity={0.6 + 0.4 * Math.sin(progress * Math.PI * 2)}
          />
        )}
        
        {/* Breath animation */}
        {breaths && (
          <>
            <path 
              d="M100 45 Q 90 30, 80 35" 
              stroke="#3B82F6" 
              strokeWidth="2" 
              fill="none"
              strokeDasharray="2"
            />
            <path 
              d="M100 45 Q 110 30, 120 35" 
              stroke="#3B82F6" 
              strokeWidth="2" 
              fill="none"
              strokeDasharray="2"
            />
            <circle cx="100" cy="45" r="8" fill="#3B82F6" opacity="0.2">
              <animate
                attributeName="r"
                values="8;12;8"
                dur="2s"
                repeatCount="indefinite"
              />
            </circle>
          </>
        )}
      </svg>
      
      {/* Hands for compressions */}
      {compressions && (
        <div 
          className="absolute left-1/2 transform -translate-x-1/2"
          style={{ 
            top: '40%',
            animation: 'compressions 1s infinite'
          }}
        >
          <div className="flex items-center justify-center">
            <div className="w-10 h-4 bg-yellow-500 rounded-md"></div>
            <div className="w-10 h-4 bg-yellow-500 rounded-md -ml-2"></div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes compressions {
          0% { transform: translate(-50%, 0); }
          50% { transform: translate(-50%, 10px); }
          100% { transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
};

const ChokingAnimation = ({ currentStep }) => {
  const backBlows = currentStep === 2;
  const abdominalThrusts = currentStep === 3;
  
  return (
    <div className="relative w-64 h-64">
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* Head */}
        <circle cx="100" cy="60" r="25" fill="none" stroke="#4B5563" strokeWidth="2" />
        
        {/* Body */}
        <line x1="100" y1="85" x2="100" y2="140" stroke="#4B5563" strokeWidth="2" />
        
        {/* Arms - different positioning for choking */}
        <line x1="100" y1="100" x2="70" y2="110" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="100" x2="130" y2="110" stroke="#4B5563" strokeWidth="2" />
        
        {/* Legs */}
        <line x1="100" y1="140" x2="80" y2="170" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="140" x2="120" y2="170" stroke="#4B5563" strokeWidth="2" />
        
        {/* Choking hands signal */}
        <circle cx="100" cy="65" r="5" fill="#EF4444" />
        
        {/* Back blows visualization */}
        {backBlows && (
          <>
            <circle cx="70" cy="95" r="8" fill="#3B82F6" opacity="0.6">
              <animate
                attributeName="r"
                values="8;12;8"
                dur="0.8s"
                repeatCount="indefinite"
              />
            </circle>
            <path 
              d="M65 90 Q 50 85, 45 100" 
              stroke="#3B82F6" 
              strokeWidth="2" 
              fill="none"
            />
          </>
        )}
        
        {/* Abdominal thrusts visualization */}
        {abdominalThrusts && (
          <>
            <rect 
              x="85" y="105" width="30" height="10" rx="2"
              fill="#EF4444" 
              opacity={0.6 + 0.4 * Math.sin(Date.now() / 200)}
            />
            <path 
              d="M85 105 Q 75 100, 70 110" 
              stroke="#EF4444" 
              strokeWidth="2" 
              fill="none"
            />
            <path 
              d="M115 105 Q 125 100, 130 110" 
              stroke="#EF4444" 
              strokeWidth="2" 
              fill="none"
            />
          </>
        )}
      </svg>
      
      {/* Helper hands for abdominal thrusts */}
      {abdominalThrusts && (
        <div 
          className="absolute left-1/2 transform -translate-x-1/2"
          style={{ top: '50%' }}
        >
          <div className="flex items-center justify-center">
            <div className="w-8 h-4 bg-yellow-500 rounded-md"></div>
            <div className="w-8 h-4 bg-yellow-500 rounded-md -ml-2"></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Simplified versions for other scenarios (would be more detailed in real implementation)
const BurnsAnimation = ({ currentStep }) => {
  const cooling = currentStep === 1;
  
  return (
    <div className="relative w-64 h-64">
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* Arm representation */}
        <path d="M80 50 Q 100 100, 120 150" stroke="#4B5563" strokeWidth="8" fill="none" />
        
        {/* Burn area */}
        <circle cx="100" cy="100" r="15" fill="#EF4444" opacity="0.7" />
        
        {/* Water droplets for cooling */}
        {cooling && (
          <>
            <circle cx="90" cy="70" r="3" fill="#3B82F6">
              <animate attributeName="cy" from="70" to="120" dur="1.5s" repeatCount="indefinite" />
            </circle>
            <circle cx="100" cy="65" r="4" fill="#3B82F6">
              <animate attributeName="cy" from="65" to="115" dur="1.2s" repeatCount="indefinite" />
            </circle>
            <circle cx="110" cy="75" r="3" fill="#3B82F6">
              <animate attributeName="cy" from="75" to="125" dur="1.7s" repeatCount="indefinite" />
            </circle>
          </>
        )}
      </svg>
    </div>
  );
};

const CardiacAnimation = ({ currentStep }) => {
  const aed = currentStep === 4;
  
  return (
    <div className="relative w-64 h-64">
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* Body outline */}
        <circle cx="100" cy="60" r="25" fill="none" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="85" x2="100" y2="140" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="100" x2="70" y2="120" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="100" x2="130" y2="120" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="140" x2="80" y2="170" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="140" x2="120" y2="170" stroke="#4B5563" strokeWidth="2" />
        
        {/* Heart with rhythm animation */}
        <path 
          d="M100 90 Q 120 70, 100 110 Q 80 70, 100 90" 
          fill="#EF4444" 
          opacity="0.7"
        >
          {aed && (
            <animate
              attributeName="opacity"
              values="0.7;1;0.7"
              dur="0.8s"
              repeatCount="indefinite"
            />
          )}
        </path>
        
        {/* AED pads */}
        {aed && (
          <>
            <rect x="85" y="95" width="10" height="15" rx="2" fill="#3B82F6" opacity="0.7" />
            <rect x="105" y="95" width="10" height="15" rx="2" fill="#3B82F6" opacity="0.7" />
            <path 
              d="M95 95 L 105 85" 
              stroke="#3B82F6" 
              strokeWidth="2" 
              strokeDasharray="4"
            >
              <animate
                attributeName="stroke-dashoffset"
                from="0"
                to="8"
                dur="1s"
                repeatCount="indefinite"
              />
            </path>
          </>
        )}
      </svg>
    </div>
  );
};

const StrokeAnimation = ({ currentStep }) => {
  const faceDroop = currentStep === 0;
  
  return (
    <div className="relative w-64 h-64">
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* Head */}
        <circle cx="100" cy="80" r="40" fill="none" stroke="#4B5563" strokeWidth="2" />
        
        {/* Asymmetrical face for stroke */}
        <circle cx="85" cy="75" r="5" fill="#4B5563" />
        <circle cx="115" cy="75" r="5" fill="#4B5563" />
        
        {/* Mouth - normal or drooped based on step */}
        <path 
          d={faceDroop ? "M85 95 Q 100 90, 115 95" : "M85 95 Q 100 105, 115 95"} 
          stroke="#4B5563" 
          strokeWidth="2" 
          fill="none" 
        />
        
        {/* Arm weakness indicator */}
        <line x1="60" y1="120" x2="90" y2="140" stroke="#4B5563" strokeWidth="2" />
        <line 
          x1="140" y1="120" 
          x2={faceDroop ? "130" : "110"} 
          y2={faceDroop ? "150" : "140"} 
          stroke="#4B5563" 
          strokeWidth="2" 
          strokeDasharray={faceDroop ? "4" : "0"}
        />
      </svg>
    </div>
  );
};

const InfantChokingAnimation = ({ currentStep }) => {
  const backBlows = currentStep === 2;
  const chestThrusts = currentStep === 3;
  
  return (
    <div className="relative w-64 h-64">
      <svg viewBox="0 0 200 200" className="w-full h-full">
        {/* Infant body - smaller proportions */}
        <circle cx="100" cy="70" r="20" fill="none" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="90" x2="100" y2="130" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="100" x2="80" y2="110" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="100" x2="120" y2="110" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="130" x2="90" y2="160" stroke="#4B5563" strokeWidth="2" />
        <line x1="100" y1="130" x2="110" y2="160" stroke="#4B5563" strokeWidth="2" />
        
        {/* Choking indicator */}
        <circle cx="100" cy="75" r="4" fill="#EF4444" />
        
        {/* Back blows */}
        {backBlows && (
          <>
            <circle cx="80" cy="100" r="6" fill="#3B82F6" opacity="0.6">
              <animate
                attributeName="r"
                values="6;10;6"
                dur="0.8s"
                repeatCount="indefinite"
              />
            </circle>
            <path 
              d="M75 95 Q 65 90, 60 105" 
              stroke="#3B82F6" 
              strokeWidth="2" 
              fill="none"
            />
          </>
        )}
        
        {/* Chest thrusts */}
        {chestThrusts && (
          <rect 
            x="90" y="105" width="20" height="8" rx="2"
            fill="#EF4444" 
            opacity={0.6 + 0.4 * Math.sin(Date.now() / 200)}
          />
        )}
      </svg>
      
      {/* Positioning guides */}
      {(backBlows || chestThrusts) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-32 h-8 bg-blue-200 bg-opacity-30 rounded-full"></div>
        </div>
      )}
    </div>
  );
};

export default FirstAidAnimation;